import React, { useState } from 'react';
import { Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  useUpdatePortalRole,
  usePortalModules,
} from '@/hooks/react-query/usePortalRoles';
import type { PortalRole, PortalModule } from '@/services/portalRoleService';

interface EditPortalRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: PortalRole | null;
  onRoleUpdated?: () => void;
}

export function EditPortalRoleDialog({
  open,
  onOpenChange,
  role,
  onRoleUpdated,
}: EditPortalRoleDialogProps) {
  const [formData, setFormData] = useState({
    display_name: role?.display_name || '',
    description: role?.description || '',
    module_ids: role?.modules?.map((m) => m.id) || [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: modulesData } = usePortalModules();
  const updateRoleMutation = useUpdatePortalRole();

  const modules = modulesData?.items || [];

  const handleClose = () => {
    setErrors({});
    onOpenChange(false);
  };

  const toggleModule = (moduleId: string) => {
    setFormData((prev) => ({
      ...prev,
      module_ids: prev.module_ids.includes(moduleId)
        ? prev.module_ids.filter((id) => id !== moduleId)
        : [...prev.module_ids, moduleId],
    }));
  };

  const handleSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();
    if (!role) return;

    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!formData.display_name.trim()) {
      newErrors.display_name = 'Role name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      await updateRoleMutation.mutateAsync({
        id: role.id,
        data: {
          display_name: formData.display_name,
          description: formData.description || undefined,
          module_ids: formData.module_ids,
        },
      });

      onOpenChange(false);
      onRoleUpdated?.();
    } catch {
      setErrors({ general: 'Failed to update role. Please try again.' });
    }
  };

  if (!role) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent key={role?.id} className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Portal Role</DialogTitle>
          <DialogDescription>
            Update the role name, description, and module access.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            {errors.general && (
              <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
                {errors.general}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="display_name">Role Name</Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    display_name: e.target.value,
                  }))
                }
                className={errors.display_name ? 'border-red-500' : ''}
              />
              {errors.display_name && (
                <p className="text-sm text-red-600">{errors.display_name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">
                Role Identifier{' '}
                <span className="text-gray-400 font-normal">(read-only)</span>
              </Label>
              <Input
                id="name"
                value={role.name}
                disabled
                className="font-mono text-sm bg-gray-50"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Describe what this role is for..."
                className="resize-none"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Module Access</Label>
              <p className="text-sm text-gray-500 mb-2">
                Select which portal modules this role can access
              </p>
              <div className="grid grid-cols-2 gap-2">
                {modules.map((module: PortalModule) => {
                  const isSelected = formData.module_ids.includes(module.id);
                  return (
                    <button
                      key={module.id}
                      type="button"
                      onClick={() => toggleModule(module.id)}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all text-left
                        ${
                          isSelected
                            ? 'bg-green-100 text-green-800 border border-green-200'
                            : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                        }
                      `}
                    >
                      {isSelected ? (
                        <Check className="h-4 w-4 shrink-0" />
                      ) : (
                        <X className="h-4 w-4 shrink-0 text-gray-400" />
                      )}
                      <span>{module.display_name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={updateRoleMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updateRoleMutation.isPending}>
              {updateRoleMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
